/**
 * /invite?token=… — accept a workspace invitation link (T-183 P3).
 * Signed out: keep the token, sign in, come back here. Signed in: preview the invite and
 * ask for confirmation (account, workspace, role, projects) — opening a link never redeems
 * it, since a single-use link taken by the wrong account is lost (T-191). Accept redeems
 * once, refreshes the token claims and the workspace list, then opens the workspace.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { WorkspaceProvider, useWorkspace } from "../contexts/WorkspaceContext";
import { InviteConfirm } from "../components/invite/InviteConfirm";
import { previewInvite, redeemInvite, type InvitePreview, type RedeemInviteResponse } from "../services/naraDoor";
import { callableErrorCode, clearInviteToken, readInviteToken, redeemErrorMessage, storeInviteToken } from "../lib/invite";

function InviteRedeem() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { user, loading, signOut } = useAuth();
  const { workspaces, isLoading, refreshWorkspaces } = useWorkspace();
  const [token] = useState(() => params.get("token") ?? readInviteToken());
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [seat, setSeat] = useState<RedeemInviteResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Preview is read-only, but StrictMode's second effect run should not call it twice.
  const previewStarted = useRef(false);

  useEffect(() => {
    if (loading) return;
    if (!token) {
      setError(t("invite.errors.missing"));
      return;
    }
    // Also the landing after "Use another account": signing out drops the user here.
    if (!user) {
      storeInviteToken(token);
      navigate("/login", { replace: true, state: { from: location } });
      return;
    }
    if (previewStarted.current) return;
    previewStarted.current = true;
    (async () => {
      try {
        setPreview(await previewInvite(token));
      } catch (err) {
        console.error("previewInvite failed", callableErrorCode(err));
        clearInviteToken();
        setError(redeemErrorMessage(err, t));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- preview once per mount
  }, [loading, user, token]);

  // A link is single-use: the button is disabled while accepting, so this runs once.
  const accept = async () => {
    if (!token || !user) return;
    setAccepting(true);
    try {
      const redeemed = await redeemInvite(token);
      clearInviteToken();
      // The new seat must reach the token claims before the workspace list is read.
      await user.getIdToken(true);
      await refreshWorkspaces();
      setSeat(redeemed);
    } catch (err) {
      console.error("redeemInvite failed", callableErrorCode(err));
      clearInviteToken();
      setError(redeemErrorMessage(err, t));
    }
  };

  const switchAccount = async () => {
    if (!token) return;
    storeInviteToken(token);
    try {
      await signOut();
    } catch (err) {
      console.error("invite signOut failed", (err as { code?: string } | null)?.code ?? "");
      setError(t("invite.errors.signOutFailed"));
    }
  };

  const cancel = () => {
    clearInviteToken();
    navigate("/", { replace: true });
  };

  // Open the redeemed workspace once the refreshed list holds it.
  useEffect(() => {
    if (!seat || isLoading) return;
    const workspace = workspaces.find((ws) => ws.id === seat.workspaceId);
    if (workspace?.handle) navigate(`/ws/${workspace.handle}/dashboard`, { replace: true });
    else setError(t("invite.errors.openFailed"));
  }, [seat, workspaces, isLoading, navigate, t]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4 text-center">
      {error ? (
        <>
          <p className="text-sm text-red-600 max-w-md">{error}</p>
          <button onClick={() => navigate("/", { replace: true })} className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700">
            {t("invite.goToNara")}
          </button>
        </>
      ) : preview && !seat && !accepting ? (
        <InviteConfirm
          preview={preview}
          account={user?.email ?? user?.phoneNumber ?? null}
          onAccept={accept}
          onSwitchAccount={switchAccount}
          onCancel={cancel}
        />
      ) : (
        <>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-sm text-muted-foreground">
            {seat ? t("invite.opening") : accepting ? t("invite.joining") : t("invite.confirm.loading")}
          </p>
        </>
      )}
      <p className="text-xs text-muted-foreground">{t("invite.recorderHint")}</p>
    </div>
  );
}

export function InvitePage() {
  return (
    <WorkspaceProvider>
      <InviteRedeem />
    </WorkspaceProvider>
  );
}
